'use client';

import { Brand, Category } from '@/payload-types';
import { WithSearchParams } from '@/types/WithSearchParams';
import { ProductListSearchParams } from '@/blocks/ProductList/types';
import { pseudoI18n } from '@/i18n/pseudoI18n';
import { Check, ChevronDown, X } from 'lucide-react';
import { clsx } from 'clsx';
import { Fragment, useState } from 'react';
import { cn } from '@/utilities/cn';
import { Sort } from '@/icons/Sort';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { buildCategoryHierarchy } from '@/blocks/ProductList/utils/buildCategoryHierarchy';
import ProductListFilterButton from '@/blocks/ProductList/components/ProductListFilterItem';
import ProductListFilterItem from '@/blocks/ProductList/components/ProductListFilterItem';

interface IProductFilterProps extends WithSearchParams<ProductListSearchParams> {
  categories: Category[];
  brands: Brand[];
}

export default function ProductListFilter( props: IProductFilterProps ) {
  const { categories, brands, searchParams, params } = props;
  const { c: searchCategories, b: searchBrands } = searchParams;

  const [ categoriesExpanded, setCategoriesExpanded ] = useState( true );
  const [ brandsExpanded, setBrandsExpanded ] = useState( true );

  const [ showFilterMobile, setShowFilterMobile ] = useState( false );
  const toggleFilterMobile = () => setShowFilterMobile( ! showFilterMobile );

  const { t } = pseudoI18n();
  const router = useRouter();

  const categoryWithHierarchy = buildCategoryHierarchy( categories );

  const onClickWrapper = ( itemId: string, queryKey: 'c' | 'b' ) => {
    return () => {
      const sp = new URLSearchParams( window.location.search );
      const currentSearch = sp.get( queryKey );
      const updatedSearch =
        currentSearch
          ?.split( ',' )
          ?.filter( Boolean )
          ?.filter( id => id !== itemId ) || [];

      // Find the selected category
      const selectedCategory = categoryWithHierarchy.find( cat => cat.id === itemId );

      // If the category has children, remove them from the updatedSearch
      if ( selectedCategory?.children ) {
        selectedCategory.children.forEach( child => {
          const childIndex = updatedSearch.indexOf( child.id );
          if ( childIndex !== -1 ) {
            updatedSearch.splice( childIndex, 1 ); // Remove the child from the selection
          }
        } );
      }

      // If the category is being added, include it and its children
      if ( ! currentSearch?.split( ',' ).includes( itemId ) ) {
        updatedSearch.push( itemId );
        if ( selectedCategory?.children ) {
          selectedCategory.children.forEach( child => {
            if ( ! updatedSearch.includes( child.id ) ) {
              updatedSearch.push( child.id );
            }
          } );
        }
      }

      if ( updatedSearch.length ) {
        sp.set( queryKey, updatedSearch.join( ',' ) );
      } else {
        sp.delete( queryKey );
      }

      const slug = ( typeof params?.slug === 'object' ? params.slug.join( '/' ) : params.slug ) || '';

      router.push( `/${ slug }?${ sp.toString() }` );
    };
  };

  return (
    <div className="relative">
      <button
        onClick={ toggleFilterMobile }
        className="bg-white absolute md:static flex h-8 items-center px-2.5 font-semibold text-sm rounded-md border border-neutral-400 gap-1 md:hidden whitespace-nowrap z-10 md:-z-10"
      >
        { t( 'Filters' ) } <Sort width={ 18 } height={ 18 } />
      </button>
      <div
        className={ cn(
          'fixed inset-0 md:static md:w-60 bg-white hidden md:block z-50 p-4 md:p-0',
          showFilterMobile && 'block',
        ) }
      >
        <div className="flex md:hidden items-center pb-2.5 mb-4 border-b border-neutral-200">
          <p className="text-lg font-bold">{ t( 'Product Filters' ) }</p>
          <Link
            className="ml-auto text-secondary text-sm mr-4 underline font-medium"
            href={ `/${ params?.slug || '' }` }
          >
            { t( 'Clear all' ) }
          </Link>
          <button onClick={ toggleFilterMobile }>
            <X />
          </button>
        </div>
        <button
          className="font-semibold cursor-pointer flex items-center justify-between w-full"
          onClick={ () => setCategoriesExpanded( ! categoriesExpanded ) }
        >
          { t( 'Product Categories' ) }
          <ChevronDown
            width={ 16 }
            height={ 16 }
            className={ clsx( 'transition-all duration-500', categoriesExpanded && '-rotate-180' ) }
          />
        </button>
        <div
          className={ cn(
            'transition-all duration-500 grid grid-rows-0fr',
            categoriesExpanded && 'grid-rows-1fr',
          ) }
        >
          <div className="overflow-hidden">
            <div className=" flex flex-col gap-2 overflow-y-auto mt-3 pb-2 border-b border-neutral-200">
              { categoryWithHierarchy?.map( category => (
                <ProductListFilterItem
                  key={ category.id }
                  queryKey="c"
                  item={ category }
                  searchString={ searchCategories }
                  onClickWrapper={ onClickWrapper }
                />
              ) ) }
            </div>
          </div>
        </div>

        <button
          className="font-semibold cursor-pointer flex items-center justify-between w-full mt-4"
          onClick={ () => setBrandsExpanded( ! brandsExpanded ) }
        >
          { t( 'Brands' ) }
          <ChevronDown
            width={ 16 }
            height={ 16 }
            className={ clsx( 'transition-all duration-500', brandsExpanded && '-rotate-180' ) }
          />
        </button>
        <div
          className={ cn(
            'transition-all duration-500 grid grid-rows-0fr',
            brandsExpanded && 'grid-rows-1fr',
          ) }
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-2 overflow-y-auto mt-3">
              { brands?.map( brand => (
                <ProductListFilterItem
                  key={ brand.id }
                  queryKey="b"
                  item={ brand }
                  searchString={ searchBrands }
                  onClickWrapper={ onClickWrapper }
                />
              ) ) }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
